import React from 'react';
import { IApiService, PagingRequest } from './base';
import {
  CreatePaymentReqBody,
  CreatePaymentResponse,
  Payment,
} from './payments/interface';
import { CreatePaymentRequest, ListPaymentsRequest } from './payments/requests';
import { IApiListResponse } from './policies/new/service';
import {
  CreatePaymentMethodRequest,
  CreatePolicyPaymentMethodRequest,
  ListBankAccountRequest,
  ListPaymentMethodRequest,
  ListPolicyPaymentMethodRequest,
  ActivatePolicyPaymentMethodRequest,
} from './payment-method/payment-methods';
import {
  BankAccount,
  PaymentMethodClass,
  CreatePaymentMethodReqBody,
  CreatePaymentMethodResponse,
  CreatePolicyPaymentMethodReqBody,
  CreatePolicyPaymentMethodResponse,
  PolicyPaymentMethod,
  ActivatePolicyPaymentMethodReqBody,
  ActivatePolicyPaymentMethodResponse,
} from './payment-method/interfaces';
import {
  CreateCreditAllocationReqBody,
  CreateCreditAllocationResponse,
  CreditAllocation,
} from './credit-allocations/interfaces';
import {
  CreateCreditAllocationRequest,
  ListCreditAllocationsRequest,
  GetCreditAllocationRequest,
} from './credit-allocations/requests';

export interface IPaymentService {
  createPayment( payload: CreatePaymentReqBody, idempotencyKey: string ): Promise<CreatePaymentResponse>;
  createPaymentMethod(
    payload: CreatePaymentMethodReqBody,
  ): Promise<CreatePaymentMethodResponse>;
  createPolicyPaymentMethod(
    payload: CreatePolicyPaymentMethodReqBody,
  ): Promise<CreatePolicyPaymentMethodResponse>;
  activatePolicyPaymentMethod(
    code: string,
    payload: ActivatePolicyPaymentMethodReqBody,
  ): Promise<ActivatePolicyPaymentMethodResponse>;
  listPayments( paging: PagingRequest ): Promise<IApiListResponse<Payment>>;
  listPaymentMethods( paging: PagingRequest ): Promise<IApiListResponse<PaymentMethodClass>>;
  listPolicyPaymentMethods( paging: PagingRequest ): Promise<IApiListResponse<PolicyPaymentMethod>>;
  listBankAccounts( paging: PagingRequest, expand?: string[] ): Promise<IApiListResponse<BankAccount>>;
  createCreditAllocation( payload: CreateCreditAllocationReqBody ): Promise<CreateCreditAllocationResponse>;
  listCreditAllocations( paging: PagingRequest ): Promise<IApiListResponse<CreditAllocation>>;
  getCreditAllocation( code: string ): Promise<CreateCreditAllocationResponse>;
}

export class PaymentsService implements IPaymentService{
  protected api: IApiService;
  constructor( api: IApiService ) {
    this.api = api;
  }

  async createPayment( payload: CreatePaymentReqBody, idempotencyKey: string ): Promise<CreatePaymentResponse> {
    const response = await this.api.request( new CreatePaymentRequest( payload, idempotencyKey ) );

    return response;
  }

  async createPaymentMethod(
    payload: CreatePaymentMethodReqBody,
  ): Promise<CreatePaymentMethodResponse> {
    const response = await this.api.request( new CreatePaymentMethodRequest( payload ) );

    return response;
  }

  async createPolicyPaymentMethod(
    payload: CreatePolicyPaymentMethodReqBody,
  ): Promise<CreatePolicyPaymentMethodResponse> {
    const response = await this.api.request( new CreatePolicyPaymentMethodRequest( payload ) );

    return response;
  }

  async activatePolicyPaymentMethod(
    code: string,
    payload: ActivatePolicyPaymentMethodReqBody,
  ): Promise<ActivatePolicyPaymentMethodResponse> {
    const response = await this.api.request( new ActivatePolicyPaymentMethodRequest( code, payload ) );

    return response;
  }

  async listPayments( paging: PagingRequest ): Promise<IApiListResponse<Payment>> {
    const response = await this.api.request( new ListPaymentsRequest( paging ) );

    return response;
  }

  async listPaymentMethods( paging: PagingRequest ): Promise<IApiListResponse<PaymentMethodClass>> {
    const response = await this.api.request( new ListPaymentMethodRequest( paging ) );

    return response;
  }

  async listPolicyPaymentMethods(
    paging: PagingRequest,
    expand?: string[],
  ): Promise<IApiListResponse<PolicyPaymentMethod>> {
    const response = await this.api.request( new ListPolicyPaymentMethodRequest( paging ) );

    return response;
  }

  async listBankAccounts( paging: PagingRequest, expand?: string[] ): Promise<IApiListResponse<BankAccount>> {
    const response = await this.api.request( new ListBankAccountRequest( paging, expand ) );

    return response;
  }

  async createCreditAllocation( payload: CreateCreditAllocationReqBody ): Promise<CreateCreditAllocationResponse> {
    const response = await this.api.request( new CreateCreditAllocationRequest( payload ) );

    return response;
  }

  async listCreditAllocations( paging: PagingRequest ): Promise<IApiListResponse<CreditAllocation>> {
    const response = await this.api.request( new ListCreditAllocationsRequest( paging ) );

    return response;
  }

  async getCreditAllocation( code: string ): Promise<CreateCreditAllocationResponse> {
    const response = await this.api.request( new GetCreditAllocationRequest( code ) );

    return response;
  }
}

export const PaymentServiceContext: React.Context<IPaymentService> = React.createContext( undefined as any );

export const usePaymentsService = (): IPaymentService => {
  return React.useContext( PaymentServiceContext );
};
